import { create } from 'zustand';

/**
 * Storefront toast notifications (SRS v2 §4.5, App. H "Add to Cart Flow"). A Zustand
 * store, not context, so any caller — a cart action, a hook, an event handler — can
 * fire `toast.success(...)` without a provider in scope. Toasts may carry one action
 * link (e.g. "View Cart").
 */
export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  to: string;
}

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  action?: ToastAction;
}

/** How long a toast stays before auto-dismissing (App. H specifies ~4s). */
const TTL_MS = 4000;

interface ToastState {
  toasts: Toast[];
  push: (message: string, variant: ToastVariant, action?: ToastAction) => void;
  dismiss: (id: number) => void;
}

let seq = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (message, variant, action) => {
    const id = (seq += 1);
    set((s) => ({ toasts: [...s.toasts, { id, message, variant, action }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })), TTL_MS);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Fire-and-forget toast API, callable from anywhere. */
export const toast = {
  success: (message: string, action?: ToastAction) =>
    useToastStore.getState().push(message, 'success', action),
  error: (message: string, action?: ToastAction) =>
    useToastStore.getState().push(message, 'error', action),
  info: (message: string, action?: ToastAction) =>
    useToastStore.getState().push(message, 'info', action),
};
